import { queryKeys } from "@/constants/queryKeyFactory";
import { useMutation, useQuery } from "@/hooks/api";
import {
  ApiResponse,
  PaginationParams,
  QueryOptions,
} from "@/interfaces/api.interface";
import { IArea, ILevel } from "@/interfaces/level.interface";
import { queryClient } from "@/lib/queryClient";
import { levelSvc } from "@/services/level.service";

export function useLevels(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ILevel[]>>(
    queryKeys.levels.list(params),
    () =>
      levelSvc.get<ILevel>({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        sort: params.sort,
        filter: params.filter,
        searchFilters: params.searchFilters,
      }),
    options
  );
}

export function useAllLevels(
  params: PaginationParams = {},
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ILevel[]>>(
    options.forExport
      ? queryKeys.levels.export({ ...params })
      : queryKeys.levels.all({ ...params }),
    ({ signal }) => levelSvc.getAll<ILevel>(params, signal),
    {
      ...options,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    }
  );
}

export function useDistinctActiveLevelNames(
  projectId: string,
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<string[]>>(
    queryKeys.levels.distinctActiveNames(projectId),
    () => levelSvc.getDistinctActiveLevelNames(projectId),
    {
      ...options,
      enabled: !!projectId && options.enabled !== false,
      staleTime: 5 * 60 * 1000, // 5 minutes cache
    }
  );
}

export function useCreateLevel(options: QueryOptions = {}) {
  return useMutation<ApiResponse<ILevel>, ILevel>(
    (data: ILevel) => levelSvc.create<ILevel>(data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.root,
        });
      },
    }
  );
}

export function useUpdateLevel(options: QueryOptions = {}) {
  return useMutation<ApiResponse<ILevel>, { id: string; data: ILevel }>(
    ({ id, data }) => levelSvc.update<ILevel>(id, data),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.all,
          refetchType: "active",
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.root,
          refetchType: "active", // Include as query keys
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.levelHistory.root,
          refetchType: "active", // Include as query keys
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.levels.areas,
          refetchType: "active", // Include as query keys
        });
      },
    }
  );
}

export function useAreasByLevel(
  levelId: string | undefined | null,
  version: number | undefined | null,
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<IArea[]>>(
    queryKeys.levels.areas(levelId || "", version || 1), // Include as query keys
    () => levelSvc.fetchAreasByLevel(levelId!, version || 1, "", ""),
    {
      ...options,
      enabled: !!levelId && options.enabled !== false,
    }
  );
}

export function useHistoryByLevel(
  levelId: string | null,
  options: QueryOptions = {}
) {
  return useQuery<ApiResponse<ILevel[]>>(
    queryKeys.levelHistory.detail(levelId!), // Include as query keys
    () => levelSvc.getHistoryByLevelId(levelId!),
    {
      ...options,
      enabled: !!levelId && options.enabled !== false,
    }
  );
}
//
